import React, { useEffect, useRef, useState } from 'react';
import { X, Save, Loader2, AlertCircle } from 'lucide-react';
import {
  atlasApi, AtlasUser, Colaborador, Ocorrencia, OcorrenciaTipo,
  OcorrenciaGravidade, OcorrenciaStatus, LABEL_OCORRENCIA,
} from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  ocorrencia?: Ocorrencia | null;
  /** Fixa o colaborador (uso na ficha) — esconde o seletor. */
  funccodiFixo?: number;
  nomeFixo?: string;
  onClose: () => void;
  onSalvo: () => void;
}

const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400';
const labelCls = 'text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1 block';

const TIPOS = Object.keys(LABEL_OCORRENCIA) as OcorrenciaTipo[];

const ModalOcorrencia: React.FC<Props> = ({ user, ocorrencia, funccodiFixo, nomeFixo, onClose, onSalvo }) => {
  const ed = ocorrencia || null;
  const hoje = new Date().toISOString().slice(0, 10);

  const [funccodi, setFunccodi] = useState<number | null>(ed?.funccodi ?? funccodiFixo ?? null);
  const [nome, setNome] = useState(ed?.colaborador_nome || nomeFixo || '');
  const [busca, setBusca] = useState('');
  const [opcoes, setOpcoes] = useState<Colaborador[]>([]);
  const [aberto, setAberto] = useState(false);
  const debRef = useRef<any>(null);

  const [form, setForm] = useState({
    tipo: (ed?.tipo || 'advertencia_verbal') as OcorrenciaTipo,
    data: ed?.data?.slice(0, 10) || hoje,
    motivo: ed?.motivo || '',
    descricao: ed?.descricao || '',
    gravidade: (ed?.gravidade || 'leve') as OcorrenciaGravidade,
    dias_suspensao: ed?.dias_suspensao != null ? String(ed.dias_suspensao) : '',
    data_inicio_suspensao: ed?.data_inicio_suspensao?.slice(0, 10) || '',
    aplicada_por: ed?.aplicada_por || '',
    testemunhas: (ed?.testemunhas || []).join(', '),
    status: (ed?.status || 'registrada') as OcorrenciaStatus,
    ciente_em: ed?.ciente_em?.slice(0, 10) || '',
    documento_url: ed?.documento_url || '',
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const set = (k: keyof typeof form, v: any) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!aberto || funccodiFixo !== undefined) return;
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => {
      atlasApi.listarColaboradores(user, { busca: busca || undefined, status: 'ativo', per_page: 8, page: 1 })
        .then(r => setOpcoes(r.itens || []))
        .catch(() => setOpcoes([]));
    }, 250);
    return () => { if (debRef.current) clearTimeout(debRef.current); };
  }, [user, busca, aberto, funccodiFixo]);

  const ehSuspensao = form.tipo === 'suspensao';

  const salvar = async () => {
    if (funccodi == null) { setErro('Selecione o colaborador.'); return; }
    if (!form.motivo.trim()) { setErro('Motivo é obrigatório.'); return; }
    if (ehSuspensao && !form.dias_suspensao) { setErro('Informe os dias de suspensão.'); return; }

    setSalvando(true);
    setErro(null);
    const body = {
      funccodi,
      tipo: form.tipo,
      data: form.data,
      motivo: form.motivo.trim(),
      descricao: form.descricao,
      gravidade: form.gravidade,
      dias_suspensao: ehSuspensao && form.dias_suspensao ? Number(form.dias_suspensao) : null,
      data_inicio_suspensao: ehSuspensao ? (form.data_inicio_suspensao || null) : null,
      aplicada_por: form.aplicada_por,
      testemunhas: form.testemunhas.split(',').map(t => t.trim()).filter(Boolean),
      status: form.status,
      ciente_em: form.ciente_em || null,
      documento_url: form.documento_url,
    };
    try {
      if (ed) await atlasApi.editarOcorrencia(user, ed._id, body);
      else await atlasApi.criarOcorrencia(user, body);
      onSalvo();
    } catch (e: any) {
      setErro(e.message || 'Erro ao salvar.');
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="font-bold text-gray-900 flex-1">{ed ? 'Editar ocorrência' : 'Nova ocorrência'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-3">
          {/* Colaborador */}
          <div>
            <label className={labelCls}>Colaborador *</label>
            {funccodiFixo !== undefined || funccodi != null ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-purple-200 bg-purple-50">
                <span className="text-sm text-gray-800 truncate">
                  {nome || `Mat. ${funccodi}`}<span className="text-gray-400 ml-2">#{funccodi}</span>
                </span>
                {funccodiFixo === undefined && !ed && (
                  <button onClick={() => { setFunccodi(null); setNome(''); setBusca(''); }} className="text-xs text-rose-600 hover:underline">
                    Trocar
                  </button>
                )}
              </div>
            ) : (
              <div className="relative">
                <input
                  className={inputCls}
                  placeholder="Buscar colaborador por nome..."
                  value={busca}
                  onFocus={() => setAberto(true)}
                  onChange={e => { setBusca(e.target.value); setAberto(true); }}
                />
                {aberto && opcoes.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                    {opcoes.map(c => (
                      <button
                        key={c._id}
                        onClick={() => {
                          setFunccodi(c.funccodi);
                          setNome(c.dados_publicos?.nome || '');
                          setAberto(false);
                          setBusca('');
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm"
                      >
                        <div className="font-medium text-gray-800 truncate">{c.dados_publicos?.nome}</div>
                        <div className="text-xs text-gray-400">
                          {c.dados_publicos?.cargo_nome || '—'} · Mat. {c.dados_publicos?.matricula || c.funccodi}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Tipo *</label>
              <select className={inputCls} value={form.tipo} onChange={e => set('tipo', e.target.value)}>
                {TIPOS.map(t => <option key={t} value={t}>{LABEL_OCORRENCIA[t]}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Data *</label>
              <input type="date" className={inputCls} value={form.data} onChange={e => set('data', e.target.value)} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Motivo *</label>
            <input className={inputCls} value={form.motivo} onChange={e => set('motivo', e.target.value)} placeholder="Atraso reiterado, descumprimento de norma..." />
          </div>

          <div>
            <label className={labelCls}>Descrição / relato dos fatos</label>
            <textarea className={`${inputCls} min-h-[90px] resize-y`} value={form.descricao} onChange={e => set('descricao', e.target.value)} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Gravidade</label>
              <select className={inputCls} value={form.gravidade} onChange={e => set('gravidade', e.target.value)}>
                <option value="leve">Leve</option>
                <option value="media">Média</option>
                <option value="grave">Grave</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Aplicada por</label>
              <input className={inputCls} value={form.aplicada_por} onChange={e => set('aplicada_por', e.target.value)} placeholder="Gestor responsável" />
            </div>

            {ehSuspensao && (
              <>
                <div>
                  <label className={labelCls}>Dias de suspensão *</label>
                  <input type="number" min={1} max={30} className={inputCls} value={form.dias_suspensao} onChange={e => set('dias_suspensao', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Início da suspensão</label>
                  <input type="date" className={inputCls} value={form.data_inicio_suspensao} onChange={e => set('data_inicio_suspensao', e.target.value)} />
                </div>
              </>
            )}

            <div>
              <label className={labelCls}>Status</label>
              <select className={inputCls} value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="registrada">Registrada</option>
                <option value="ciente">Ciente (assinada)</option>
                <option value="contestada">Contestada</option>
                <option value="anulada">Anulada</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Data da ciência</label>
              <input type="date" className={inputCls} value={form.ciente_em} onChange={e => set('ciente_em', e.target.value)} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Testemunhas (separadas por vírgula)</label>
            <input className={inputCls} value={form.testemunhas} onChange={e => set('testemunhas', e.target.value)} />
          </div>

          <div>
            <label className={labelCls}>Link do documento assinado</label>
            <input className={inputCls} value={form.documento_url} onChange={e => set('documento_url', e.target.value)} placeholder="https://..." />
          </div>

          {erro && <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm"><AlertCircle className="w-4 h-4" /> {erro}</div>}

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
            <button onClick={salvar} disabled={salvando} className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
              {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalOcorrencia;
